import { NumberNoNaN } from '@fintronners/react-utils/src/numberUtilsTSX';
import { V1TradeChargeType } from '@fintronners/react-api/src';
import { TradeSide } from '@/components/common/SidePanels/TradeSidePanel/schema';
import { usePreviewOrderCostStore } from '@/stores/trades/preview-order-cost';
import { usePreviewTradeFeeStore } from '@/stores/trades/preview-trade-fee';

export interface FixedIncomeTradeBreakdownParams {
  side: TradeSide;
  amount: number;
  faceValue: number | undefined;
  yieldToWorst: number;
  tradableUnits: number;
}

export interface FixedIncomeTradeBreakdown {
  markup: number;
  commission: number;
  feeAmount: number;
  grossOrderAmount: number;
  netOrderAmount: number;
  accruedInterestPerBond: number;
  faceValue: number | undefined;
  yieldToWorst: number;
  tradableUnits: number;
}

/**
 * Hook to calculate Fixed Income trade breakdown including fees and amounts.
 */
export const useFixedIncomeTradeBreakdown = (
  params: FixedIncomeTradeBreakdownParams,
): FixedIncomeTradeBreakdown => {
  const { side, amount, faceValue, yieldToWorst, tradableUnits } = params;

  const previewOrderCost = usePreviewOrderCostStore((state) => state.data);
  const previewTradeFee = usePreviewTradeFeeStore((state) => state.data);

  const feeAmount = NumberNoNaN(previewTradeFee?.feeAmount);
  const markup = previewTradeFee?.feeType === V1TradeChargeType.MarkUp ? feeAmount : 0;
  const commission =
    previewTradeFee?.feeType === V1TradeChargeType.Commission ? feeAmount : 0;
  const accruedInterestPerBond = NumberNoNaN(previewOrderCost?.estimatedAccruedInterest) / amount;
  const grossOrderAmount = NumberNoNaN(previewOrderCost?.estimatedNetAmount);

  let netOrderAmount = grossOrderAmount;

  if (grossOrderAmount > 0) {
    // For BUY: user pays fee with their balance
    // For SELL: user receives amount minus fee
    netOrderAmount += side === TradeSide.BUY ? feeAmount : -feeAmount;
  }

  return {
    feeAmount,
    markup,
    commission,
    grossOrderAmount,
    netOrderAmount,
    accruedInterestPerBond,
    faceValue,
    yieldToWorst,
    tradableUnits,
  };
};
